import { authStorage } from "./authStorage";
import { resolveAuthUserId } from "./authUtils";
import type { AuthUser } from "./types";

/**
 * Where a user was when the system logged them out (idle timeout, expired
 * session, a 401), so the next login can put them back there instead of on
 * the dashboard. The Logout button never saves one: leaving on purpose is not
 * something to resume.
 *
 * sessionStorage on purpose: it belongs to this tab only and is gone when the
 * tab closes, so a path never follows a user to another day or another window.
 */
const STORAGE_KEY = "maleva_return_path";

type SavedReturnPath = {
  path: string;
  userId: number | null;
};

const isResumablePath = (path: string): boolean =>
  // Same-origin app paths only ("//host" would leave the site), and not the
  // login page itself.
  path.startsWith("/") && !path.startsWith("//") && path !== "/";

export const returnPath = {
  /**
   * Call BEFORE the session is cleared: the saved path is tied to the user who
   * is still in storage at that moment.
   */
  save(path: string = `${window.location.pathname}${window.location.search}`): void {
    if (!isResumablePath(path)) {
      return;
    }

    try {
      const saved: SavedReturnPath = {
        path,
        userId: resolveAuthUserId(authStorage.getAuth()),
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
    } catch {
      // Storage unavailable: the login simply lands on the dashboard.
    }
  },

  /**
   * The saved path for this user, removed as it is read. Null when there is
   * none, or when someone else logged in on this browser: they must not land
   * on the previous user's screen.
   */
  consume(user: AuthUser | null): string | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const saved = JSON.parse(raw) as Partial<SavedReturnPath>;
      if (typeof saved.path !== "string" || !isResumablePath(saved.path)) {
        return null;
      }

      const userId = resolveAuthUserId(user);
      if (saved.userId == null || userId == null || saved.userId !== userId) {
        return null;
      }

      return saved.path;
    } catch {
      return null;
    }
  },

  clear(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // nothing to clear
    }
  },
};
